import { Box, BoxProps, useTheme } from "@mui/material";
import { PropsWithChildren } from "react";

type Anchor = "left" | "right" | "top" | "bottom";

type PanelProps = PropsWithChildren<
  Omit<BoxProps, "width" | "height"> & {
    open: boolean;
    anchor?: Anchor;
    size?: BoxProps["width"];
  }
>;

const isHorizontal = (anchor: Anchor): boolean =>
  anchor === "left" || anchor === "right";

const borderByAnchor: Record<Anchor, string> = {
  right: "borderLeft",
  left: "borderRight",
  top: "borderBottom",
  bottom: "borderTop",
};

export const Panel: React.FC<PanelProps> = ({
  children,
  open,
  anchor = "right",
  size = "30%",
  sx,
  ...boxProps
}) => {
  const theme = useTheme();
  const horizontal = isHorizontal(anchor);
  const borderSide = borderByAnchor[anchor];

  const openSize = open ? size : 0;
  const sizeSx = horizontal
    ? { width: openSize, minWidth: openSize }
    : { height: openSize, minHeight: openSize };

  return (
    <Box
      sx={{
        ...sizeSx,
        flexShrink: 0,
        transition: horizontal
          ? "width 0.3s ease, min-width 0.3s ease"
          : "height 0.3s ease, min-height 0.3s ease",
        bgcolor: "background.paper",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        [borderSide]: open ? `thin solid ${theme.palette.divider}` : "none",
        ...sx,
      }}
      {...boxProps}
    >
      {children}
    </Box>
  );
};
